import { useMemo } from "react";
import { Icon, Stack, Title } from "@introist/react-foundation/v2";
import { SelectorIcon } from "../../../employees/fields/EmployeeFieldSelector";
import { SingleTagSelect } from "../../../tags/SingleTagSelect";
import { ListItem } from "../../../../foundation/blocks/ListItem";
import { Tags } from "../../../tags";
import { useTags } from "../../../employees/hooks/useTags";
import { useWorkflowFieldsTagList } from "../../fields/WorkflowFieldTagList";
import compact from "lodash.compact";

type TeamsChannelSelectProps = {
  channels: Array<{ key: string; title: string }>;
  value: string;
  onChange: (newValue: string) => unknown;
  error?: boolean;
  workflowId?: string;
};

export const TeamsChannelSelect = ({
  channels,
  value,
  onChange,
  workflowId
}: TeamsChannelSelectProps) => {
  const workflowFieldTags = useWorkflowFieldsTagList(workflowId ?? "", ["text"], true);
  const { tagTitle } = useTags(workflowId);

  const teamsChannelOptions = useMemo(
    () =>
      (channels ?? []).map(channel => ({
        key: channel.key,
        title: channel.title,
        startAdornment: <SelectorIcon icon="hashtag" />
      })),
    [channels]
  );

  const resolveTitle = (value: string) => {
    if (Tags.isTag(value)) return tagTitle(value);

    const channel = channels?.find(c => c.key === value);
    if (channel) return channel.title;

    return value;
  };

  return (
    <Stack vertical gap="xSmall">
      {value && (
        <ListItem onRemove={() => onChange("")}>
          <Stack style={{ width: "100%" }} gap="small">
            <SelectorIcon icon={Tags.isTag(value) ? "attribute" : "hashtag"} />
            <Title variant="bold">{resolveTitle(value)}</Title>
          </Stack>
        </ListItem>
      )}
      {!value && (
        <SingleTagSelect
          tagMode
          onChange={path => onChange(path)}
          placeholder="Select channel"
          fieldTypes={["text"]}
          buttonProps={{
            startAdornment: <Icon name="plusSmall" />
          }}
          customCategories={compact([
            workflowFieldTags,
            teamsChannelOptions.length > 0
              ? {
                  key: "teamschannels",
                  title: "Teams Channels",
                  options: teamsChannelOptions,
                  icon: "hashtag"
                }
              : null
          ])}
        />
      )}
    </Stack>
  );
};
